import { ScrollArea } from '~/primitives/scroll-area';
import Flag from '~/partials/Flag';
import { ListTabSwitch } from '~/partials/TabSwitch';
import ThemedImage from '~/partials/ThemedImage';
import { useTheme } from '~/components/Providers/ThemeContext';
import chroma from 'chroma-js';
import { Button } from '~/primitives/button';
import { SquareArrowRight } from 'lucide-react';
import { fromNumeric, getCountryName } from '~/util/countries';
import { getRouteApi, Link } from '@tanstack/react-router';
import { RankDisplay, CountryClanPointDisplay } from '../-shared';

const Route = getRouteApi('/profile/country/$country');

function CountryProfileSummary() {
    const { country } = Route.useParams();

    const { profile, classicTotal, platformerTotal } = Route.useLoaderData({
        select: ({ profile, classicTotal, platformerTotal }) => ({
            profile,
            classicTotal,
            platformerTotal,
        }),
    });

    const { primaryColor } = useTheme();
    const hue = chroma(primaryColor).get('hsl.h');
    const lightness = chroma(primaryColor).get('hsl.l') * 100 + 50;

    const countryData = fromNumeric(profile.country);

    return (
        <div className="flex flex-col w-full lg:w-2/6 lg:px-2">
            <ScrollArea className="lg:p-4 lg:h-[calc(100vh-150px)]">
                <div className="flex flex-col items-center text-center w-full">
                    <Flag
                        countryCode={profile.country}
                        className="min-h-60 max-h-60 min-w-60 max-w-60"
                    />
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <p className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-2xl-3xl-clamp] leading-[1!important] font-medium text-secondary-foreground">
                            {getCountryName(countryData)}
                        </p>
                    </div>
                </div>

                <ListTabSwitch
                    from="/profile/country/$country"
                    className="w-10/12 mx-auto"
                    classicDisplay={`Classic (${classicTotal})`}
                    platformerDisplay={`Platformer (${platformerTotal})`}
                    size="md"
                />

                <div className="flex justify-evenly items-center mt-6">
                    <CountryClanPointDisplay
                        label="Level Points"
                        value={profile?.rank?.level_points / 10}
                    />
                </div>

                <div className="mt-8 text-center clamp-[px-3-6-clamp]">
                    <h3 className="clamp-[text-2xl-4xl-clamp] lg:clamp-[text-lg-2xl-clamp] font-semibold uppercase">
                        <ThemedImage
                            src="/assets/globalRank.webp"
                            alt="global rank icon"
                            className="inline h-6 mr-2"
                            hueRotation={hue}
                            lightnessCorrection={lightness}
                        />
                        Rankings
                    </h3>
                    <div className="mt-2">
                        <RankDisplay
                            label="Points Rank"
                            value={profile?.rank?.rank}
                        />
                        <RankDisplay
                            label="Extremes Count Rank"
                            value={profile?.rank?.extremes_rank}
                        />
                        <RankDisplay
                            label="Hardest Level Rank"
                            value={profile?.rank?.hardest_rank}
                        />
                    </div>
                </div>
                <div className="text-center mt-4">
                    <Button asChild variant={'outline'} className=" m-4">
                        <Link
                            to="/leaderboard"
                            search={(prev) => ({
                                list: prev.list,
                                page: 1,
                                type: 'users',
                                country: Number(country),
                            })}
                        >
                            <SquareArrowRight size={20} className="mr-2" />
                            Country members leaderboard
                        </Link>
                    </Button>
                </div>
            </ScrollArea>
        </div>
    );
}

export default CountryProfileSummary;
